import { ContextualMenuItemType, type IContextualMenuItem } from '@fluentui/react';
import { Button } from '@h2oai/ui-kit';
import React from 'react';

import { ListWorkspaceSupportedFilterOrderKeys } from '../../../../authz/authz.functions';
import { NONE_OPTION_KEY, noneOption } from '../../../../utils/render';

export type WorkspaceOrderByKeys = Extract<
  ListWorkspaceSupportedFilterOrderKeys,
  'display_name' | 'create_time' | 'update_time'
>;

const orderKeyToText: Record<WorkspaceOrderByKeys, string> = {
  display_name: `Display Name`,
  create_time: `Create Time`,
  update_time: `Update Time`,
};

const sortByMenuItems: IContextualMenuItem[] = Object.entries(orderKeyToText).map(([key, text]) => ({
  key,
  text,
}));

const orderMenuItems: IContextualMenuItem[] = [
  {
    key: `ascending`,
    text: `Ascending`,
  },
  {
    key: `descending`,
    text: `Descending`,
  },
];

export type WorkspaceOrderByProps = {
  descending: boolean;
  onChange: (key: WorkspaceOrderByKeys | undefined, descending: boolean) => void;
  orderKey: WorkspaceOrderByKeys | undefined;
};

export const WorkspaceOrderBy = (props: WorkspaceOrderByProps) => {
  const { descending, onChange, orderKey } = props;
  const onMenuItemClick = (_ev: any, menuItems: IContextualMenuItem | undefined) => {
    if (!menuItems) return;
    const key = menuItems.key;
    if (key === NONE_OPTION_KEY) {
      onChange(undefined, false);
      return;
    }
    if (key === 'ascending' || key === 'descending') {
      onChange(orderKey, key === 'descending');
      return;
    }
    if (orderKeyToText[key]) {
      onChange(key as WorkspaceOrderByKeys, descending);
      return;
    }
  };
  const menuItems = React.useMemo<IContextualMenuItem[]>(() => {
    const sortByMenuItemsWithActive = sortByMenuItems.map((item) => {
      const isActive = item.key === orderKey;
      return {
        ...item,
        onClick: onMenuItemClick,
        iconProps: isActive ? { iconName: 'CheckMark' } : undefined,
      };
    });
    const orderMenuItemsWithActive = orderMenuItems.map((item) => {
      const isActive = item.key === (descending ? 'descending' : 'ascending');
      return {
        ...item,
        disabled: !orderKey,
        onClick: onMenuItemClick,
        iconProps: isActive ? { iconName: 'CheckMark' } : undefined,
      };
    });

    return [
      {
        key: 'sortBy',
        itemType: ContextualMenuItemType.Section,
        sectionProps: {
          topDivider: true,
          bottomDivider: true,
          title: 'Sort By',
          items: [
            {
              ...noneOption,
              onClick: onMenuItemClick,
            },
            ...sortByMenuItemsWithActive,
          ],
        },
      },
      {
        key: `order`,
        itemType: ContextualMenuItemType.Section,
        sectionProps: {
          title: `Order`,
          items: orderMenuItemsWithActive,
        },
      },
    ];
  }, [descending, orderKey]);

  return (
    <Button
      menuIconProps={{ iconName: !orderKey ? `Sort` : descending ? `Descending` : `Ascending` }}
      menuItems={menuItems}
      text={(orderKey && orderKeyToText[orderKey]) || `Sort By`}
    />
  );
};
